import React, {FC} from 'react';
import styled from 'styled-components';

import {buildCreateContactFormBoxStyles} from '../../../../../../../../src/helpers/createContactFormHelpers';
import {CreateContactLinkFieldModel} from '../../../../../../../../src/store/panels/contacts/createContactForm/createContactFormState';
import {Localizer} from '../../../../../../../../src/store/stateless/internationalizationSelectors';
import {useLocalize} from '../../../../../../../../srcCommon/internationalization/internationalizationContext';
import {defineMessage, Trans} from '../../../../../../../shared/helpers/src/internationalization/react';
import {useDebounce} from '../../../../../../../shared/helpers/src/react/hooks/hookHelpers';
import {Handler, HandlerOf} from '../../../../../../../shared/helpers/src/react/reactHelpers';
import {BoxedInput} from '../../../../../../../shared/ui/src/components/boxedInputs/boxedInput';
import {
  BoxedInputElement,
  BoxedInputElementTypesEnum,
} from '../../../../../../../shared/ui/src/components/boxedInputs/boxedInputElement';
import {BoxedInputIcon} from '../../../../../../../shared/ui/src/components/boxedInputs/boxedInputIcon';
import {VisualSizesEnum} from '../../../../../../../shared/ui/src/styles/commonStyles';
import {UserThemeConsumer} from '../../../../../../../shared/ui/src/theme/userThemeConsumer';
import {CreateContactInformationFieldActions} from './createContactInformationFieldActions';

/*
 * Constants.
 */

const debounceDelay = 500; // ms.

/*
 * Props.
 */

interface CreateContactLinkInputProps {
  link: CreateContactLinkFieldModel;
  isFirst: boolean;
  isLast: boolean;
  isDisabled: boolean;

  onUpdateLink: HandlerOf<string>;
  onAddNewLink: Handler;
  onRemoveLink: Handler;
}

/*
 * Style.
 */

const StyledLinkInputWrapperDiv = styled.div`
  display: flex;
  flex-flow: row;
  gap: 8px;
`;

const StyledBoxedInput = styled(BoxedInput)`
  flex: 1;
`;

const StyledBoxedInputElement = styled(BoxedInputElement)`
  margin-left: 1px;
`;

interface StyledCreateContactInformationFieldActionsProps {
  $isFirst: boolean;
}

const StyledCreateContactInformationFieldActions = styled(
  CreateContactInformationFieldActions,
)<StyledCreateContactInformationFieldActionsProps>`
  ${(p) => p.$isFirst && 'margin-top: 23px'};
`;

/*
 * Component.
 */

export const CreateContactLinkInput: FC<CreateContactLinkInputProps> = (props) => {
  const {link, isFirst, isLast, isDisabled, onUpdateLink, onAddNewLink, onRemoveLink} = props;
  const localize = useLocalize();

  const onUpdateLinkDebounced = useDebounce((value: string) => onUpdateLink(value.trim()), debounceDelay);
  const onInputBlur = () => onUpdateLinkDebounced.flush();

  return (
    <StyledLinkInputWrapperDiv>
      {renderInput(link, isFirst, isDisabled, localize, onUpdateLinkDebounced, onInputBlur)}
      {!isDisabled && (
        <StyledCreateContactInformationFieldActions
          $isFirst={isFirst}
          hasValue={Boolean(link.value)}
          isFirst={isFirst}
          isLast={isLast}
          addAnotherLabel={<Trans id="create-contact-form-link-add-link-button">Add another link</Trans>}
          onAdd={onAddNewLink}
          onRemove={onRemoveLink}
        />
      )}
    </StyledLinkInputWrapperDiv>
  );
};

/*
 * Helpers.
 */

function renderInput(
  link: CreateContactLinkFieldModel,
  isFirst: boolean,
  isDisabled: boolean,
  localize: Localizer,
  onUpdateLinkDebounced: HandlerOf<string>,
  onInputBlur: Handler,
) {
  return (
    <UserThemeConsumer>
      {(theme) => (
        <StyledBoxedInput
          label={maybeRenderInputLabel(isFirst)}
          boxStyles={buildCreateContactFormBoxStyles(isFirst)}
          isDisabled={isDisabled}
        >
          {(childProps) => (
            <>
              <BoxedInputIcon
                {...childProps}
                iconName="linkSmall"
                color={theme.greys.shade70}
                size={VisualSizesEnum.MEDIUM}
              />
              <StyledBoxedInputElement
                {...childProps}
                type={BoxedInputElementTypesEnum.TEXT}
                placeholder={localize(
                  defineMessage({
                    id: 'create-contact-form-link-placeholder',
                    message: 'Add link',
                  }),
                )}
                onChange={onUpdateLinkDebounced}
                onBlur={onInputBlur}
                size={VisualSizesEnum.MEDIUM}
                defaultValue={link.value}
              />
            </>
          )}
        </StyledBoxedInput>
      )}
    </UserThemeConsumer>
  );
}

function maybeRenderInputLabel(isFirst: boolean) {
  if (!isFirst) {
    return null;
  }
  return <Trans id="create-contact-form-links-label">Links</Trans>;
}
